import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, ArrowDownToLine, X, CheckCircle2, Trash2, TrendingUp } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * AdminDivToNonDivButton — Round P112 (Feb 2026).
 *
 * Reconciliation panel that lists every ticker in the dividend
 * universe (`pays_dividend != False`) with no dividend yield
 * (missing / null / non-positive). Explicitly does NOT rely on
 * `is_dividend_payer` — that flag has been unreliable after
 * shield-promotions.
 *
 * Per-row actions:
 *   ↑ Arrow (far LEFT)  — route to Pending Non-Dividend Review via
 *                          POST /api/admin/mark-non-dividend/{t}
 *                          (the existing reclassify handler already
 *                          deletes from stock_cache, upserts into the
 *                          pending_non_dividend_review queue, and
 *                          flips us_market_universe.is_non_dividend).
 *   🗑 Trash (far RIGHT) — permanent blocklist via
 *                          POST /api/admin/blocklist/{t}
 *                          (physically separated to prevent the
 *                          "muscle-memory click far right = trash"
 *                          reflex from destroying a queue candidate).
 *
 * The list refetches on every button open and after every mutation so
 * the admin always sees the current reconciliation state.
 */
export default function AdminDivToNonDivButton({ onStockClick }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [busyTicker, setBusyTicker] = useState(null);
  const [confirmBlocklist, setConfirmBlocklist] = useState(null);
  // P158·12 (Feb 2026) — bulk-dismiss confirmation state.
  const [confirmDismissAll, setConfirmDismissAll] = useState(false);
  const [dismissingAll, setDismissingAll] = useState(false);

  if (user?.role !== 'admin') return null;

  const fetchList = async () => {
    setLoading(true);
    setReport(null);
    setOpen(true);
    try {
      const { data } = await axios.get(
        `${API}/admin/reconcile/dividend-no-yield`,
        { withCredentials: true },
      );
      setReport(data);
    } catch (err) {
      toast.error('Could not load Div → Non-Div reconcile list', {
        description: err?.response?.data?.detail || err?.message,
      });
      setOpen(false);
    } finally {
      setLoading(false);
    }
  };

  const removeFromState = (ticker) => {
    setReport((prev) =>
      prev
        ? {
            ...prev,
            candidates: (prev.candidates || []).filter((c) => c.ticker !== ticker),
            count: Math.max(0, (prev.count || 1) - 1),
          }
        : prev,
    );
  };

  const moveToNonDiv = async (row) => {
    const t = row.ticker;
    setBusyTicker(t);
    try {
      await axios.post(
        `${API}/admin/mark-non-dividend/${encodeURIComponent(t)}`,
        { reason: 'reconcile_div_to_non_div' },
        { withCredentials: true },
      );
      toast.success(`${t} → Pending Non-Div Review`, {
        description: 'Queued — open the Pending Non-Dividend Review panel to Accept and hydrate.',
        duration: 4500,
      });
      removeFromState(t);
    } catch (err) {
      toast.error(`Could not move ${t}`, {
        description: err?.response?.data?.detail || err?.message,
      });
    } finally {
      setBusyTicker(null);
    }
  };

  const blocklist = async (row) => {
    const t = row.ticker;
    setBusyTicker(t);
    try {
      const { data } = await axios.post(
        `${API}/admin/blocklist/${encodeURIComponent(t)}`,
        { reason: 'reconcile_div_to_non_div_trash' },
        { withCredentials: true },
      );
      toast.success(`Blocklisted ${t}`, {
        description: `Removed from ${Object.keys(data?.removed || {}).length} collections.`,
        duration: 4000,
      });
      removeFromState(t);
    } catch (err) {
      toast.error(`Could not blocklist ${t}`, {
        description: err?.response?.data?.detail || err?.message,
      });
    } finally {
      setBusyTicker(null);
    }
  };

  const handleTickerClick = (ticker) => {
    if (!ticker) return;
    onStockClick?.(ticker);
  };

  // P158·12 — bulk-dismiss the current list. Stamps a hidden flag on
  // every candidate so they drop off the modal permanently (until
  // yield data comes back in, at which point they never matched the
  // filter anyway).
  const dismissAll = async () => {
    setDismissingAll(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/reconcile/dividend-no-yield/dismiss-all`,
        {},
        { withCredentials: true },
      );
      const n = Number(data?.dismissed || 0);
      toast.success(`Dismissed ${n} row${n === 1 ? '' : 's'}`, {
        description: 'Cleared from this reconcile view. Yield-bearing rows never match this list anyway.',
        duration: 4500,
      });
      setConfirmDismissAll(false);
      await fetchList();
    } catch (err) {
      toast.error('Could not dismiss list', {
        description: err?.response?.data?.detail || err?.message,
      });
    } finally {
      setDismissingAll(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={fetchList}
        disabled={loading}
        className="inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium bg-orange-500/15 hover:bg-orange-500/25 text-orange-900 dark:text-orange-200 border border-orange-500/40 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
        title="Admin: dividend-universe tickers with NO dividend yield. Arrow → Pending Non-Div Review. Trash → permanent blocklist."
        data-testid="admin-div-to-non-div-fire"
      >
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowDownToLine className="w-4 h-4" />}
        Div → Non-Div
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-start sm:items-center justify-center p-3 sm:p-6 overflow-y-auto"
          onClick={() => setOpen(false)}
          data-testid="admin-div-to-non-div-modal"
        >
          <div
            className="bg-background border border-orange-500/60 rounded-lg shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-4 py-3 border-b border-border/60 flex items-center gap-3">
              <ArrowDownToLine className="w-4 h-4 text-orange-600" />
              <div className="flex-1">
                <div className="text-sm font-bold">Div → Non-Div reconciliation</div>
                <div className="text-[10px] text-muted-foreground">
                  Dividend-universe rows with missing / zero / null dividend_yield. Arrow queues to Pending Non-Div Review; trash blocklists permanently.
                </div>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="text-muted-foreground hover:text-foreground p-1 rounded"
                data-testid="admin-div-to-non-div-close"
                aria-label="Close reconcile modal"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
              {loading && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground py-6 justify-center">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Scanning dividend universe…
                </div>
              )}

              {report && (
                <>
                  <div className="text-[11px] text-muted-foreground" data-testid="admin-div-to-non-div-count">
                    {report.count} candidate{report.count === 1 ? '' : 's'} — dividend rows with no yield.
                  </div>

                  {report.count === 0 && (
                    <div className="flex items-center gap-2 px-3 py-4 rounded-md bg-emerald-50 dark:bg-emerald-950/40 text-emerald-800 dark:text-emerald-200 text-sm">
                      <CheckCircle2 className="w-4 h-4" />
                      Every dividend-universe row has a positive yield. Nothing to reconcile.
                    </div>
                  )}

                  {report.candidates && report.candidates.length > 0 && (
                    <div className="border border-border/60 rounded-md overflow-hidden">
                      <div className="divide-y divide-border/40">
                        {report.candidates.map((row) => (
                          <div
                            key={row.ticker}
                            className="px-3 py-1.5 flex items-center gap-3 text-xs"
                            data-testid={`admin-div-to-non-div-row-${row.ticker}`}
                          >
                            {/* Arrow — FIRST. Physical separation from
                                the trash icon on the right prevents
                                the muscle-memory "click far right =
                                trash" reflex from destroying a queue
                                candidate. Sends to Pending Non-Div
                                Review, not straight to non-div. */}
                            <button
                              type="button"
                              onClick={() => moveToNonDiv(row)}
                              disabled={busyTicker === row.ticker}
                              className="text-muted-foreground hover:text-emerald-600 disabled:opacity-40 shrink-0"
                              title={`Move ${row.ticker} to the Pending Non-Dividend Review queue.`}
                              data-testid={`admin-div-to-non-div-move-${row.ticker}`}
                            >
                              {busyTicker === row.ticker
                                ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                : <TrendingUp className="w-3.5 h-3.5" />}
                            </button>
                            <button
                              type="button"
                              onClick={() => handleTickerClick(row.ticker)}
                              className="font-mono font-bold w-14 shrink-0 text-orange-700 dark:text-orange-300 hover:text-orange-500 dark:hover:text-orange-200 underline-offset-2 hover:underline text-left"
                              title={`Open ${row.ticker} in the Stock Modal`}
                              data-testid={`admin-div-to-non-div-ticker-${row.ticker}`}
                            >
                              {row.ticker}
                            </button>
                            <span className="flex-1 truncate text-muted-foreground" title={row.name}>
                              {row.name || '—'}
                            </span>
                            <span className="font-mono text-[10px] text-muted-foreground shrink-0">
                              y={row.dividend_yield ?? '—'} · r={row.dividend_rate ?? '—'}
                            </span>
                            <button
                              type="button"
                              onClick={() => setConfirmBlocklist(row)}
                              disabled={busyTicker === row.ticker}
                              className="text-muted-foreground hover:text-rose-600 disabled:opacity-40 shrink-0"
                              title={`Permanently blocklist ${row.ticker}.`}
                              data-testid={`admin-div-to-non-div-trash-${row.ticker}`}
                            >
                              {busyTicker === row.ticker
                                ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                : <Trash2 className="w-3.5 h-3.5" />}
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="text-[10px] text-muted-foreground pt-2 border-t border-border/40">
                    Audited at {report.audited_at} · list capped at 1000.
                  </div>
                </>
              )}
            </div>

            <div className="px-4 py-2 border-t border-border/60 flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={fetchList}
                  disabled={loading}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-bold bg-orange-600 hover:bg-orange-700 text-white disabled:opacity-60"
                  data-testid="admin-div-to-non-div-refresh"
                >
                  {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <ArrowDownToLine className="w-3 h-3" />}
                  Refresh
                </button>
                {/* P158·12 — Dismiss All: bulk-hide the entire current
                    list so admins can clear rows they've already
                    triaged. Confirmation modal before firing. Hidden
                    when there's nothing to dismiss. */}
                {report && report.count > 0 && (
                  <button
                    type="button"
                    onClick={() => setConfirmDismissAll(true)}
                    disabled={loading || dismissingAll}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-bold bg-zinc-200 dark:bg-zinc-700 hover:bg-zinc-300 dark:hover:bg-zinc-600 text-zinc-800 dark:text-zinc-100 disabled:opacity-60"
                    data-testid="admin-div-to-non-div-dismiss-all"
                    title="Hide every row currently in this list. Rows stay in stock_cache and re-appear only if yield data changes — but yield-present rows never match this filter anyway."
                  >
                    {dismissingAll ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
                    Dismiss All ({report.count})
                  </button>
                )}
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="text-[11px] px-2.5 py-1 rounded-md border border-border hover:bg-muted"
                data-testid="admin-div-to-non-div-dismiss"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* P158·12 — Dismiss All confirmation. Full-screen modal so it
          doesn't get lost inside the reconcile modal itself. */}
      {confirmDismissAll && (
        <div
          className="fixed inset-0 z-[110] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setConfirmDismissAll(false)}
          data-testid="admin-div-to-non-div-dismiss-confirm-modal"
        >
          <div
            className="bg-background border border-zinc-500/60 rounded-lg shadow-2xl w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 py-4 border-b border-border/60 flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-zinc-600" />
              <div className="text-sm font-bold">Dismiss all {report?.count || 0} rows?</div>
            </div>
            <div className="px-5 py-4 text-[12px] text-muted-foreground space-y-2">
              <p>
                Marks every ticker in this list as reviewed and hides them from the
                Div → Non-Div panel. Tickers stay in <code>stock_cache</code>, keep working
                everywhere else, and only re-appear here if yield data changes
                (yield-present rows don&apos;t match this filter anyway).
              </p>
              <p className="text-zinc-700 dark:text-zinc-300 font-semibold">
                Reversible via Mongo if needed.
              </p>
            </div>
            <div className="px-5 py-3 border-t border-border/60 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setConfirmDismissAll(false)}
                className="px-3 py-1.5 rounded-md text-[12px] font-bold border border-border hover:bg-muted"
                data-testid="admin-div-to-non-div-dismiss-cancel"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={dismissAll}
                disabled={dismissingAll}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12px] font-bold bg-zinc-700 hover:bg-zinc-800 text-white disabled:opacity-60"
                data-testid="admin-div-to-non-div-dismiss-confirm"
              >
                {dismissingAll ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
                Dismiss all
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmBlocklist && (
        <div
          className="fixed inset-0 z-[110] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setConfirmBlocklist(null)}
          data-testid="admin-div-to-non-div-blocklist-confirm-modal"
        >
          <div
            className="bg-background border border-rose-500/60 rounded-lg shadow-2xl w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 py-4 border-b border-border/60 flex items-center gap-2">
              <Trash2 className="w-4 h-4 text-rose-600" />
              <div className="text-sm font-bold">Permanently blocklist ticker?</div>
            </div>
            <div className="px-5 py-4 text-[12px] text-muted-foreground space-y-2">
              <div>
                <span className="font-mono font-bold text-foreground">{confirmBlocklist.ticker}</span>
                {' — '}
                <span>{confirmBlocklist.name}</span>
              </div>
              <p className="pt-2 text-rose-700 dark:text-rose-400 font-semibold">
                Permanent. Use only for tickers that clearly don&apos;t belong.
              </p>
            </div>
            <div className="px-5 py-3 border-t border-border/60 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setConfirmBlocklist(null)}
                className="px-3 py-1.5 rounded-md text-[12px] font-bold border border-border hover:bg-muted"
                data-testid="admin-div-to-non-div-blocklist-cancel"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  const row = confirmBlocklist;
                  setConfirmBlocklist(null);
                  blocklist(row);
                }}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12px] font-bold bg-rose-600 hover:bg-rose-700 text-white"
                data-testid="admin-div-to-non-div-blocklist-confirm"
              >
                <Trash2 className="w-3 h-3" />
                Remove and blocklist
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
